import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react"
import * as Dialog from "@radix-ui/react-dialog"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { toast } from "sonner"
import {
  AudioLines,
  Camera,
  CircleHelp,
  Clapperboard,
  Loader2,
  Magnet,
  Repeat,
  Scissors,
  Trash2,
  Wand2,
  X,
} from "lucide-react"
import { SyncSoIcon } from "@/components/icons/sync-so-icon"
import {
  PlaybackTransport,
  PlayerIconButton,
  usePlaybackTransport,
} from "@/components/timeline-player/playback-transport"
import { PlayerPipButton, PlayerPipOverlay, PlayerPipProvider } from "@/components/timeline-player/player-pip"
import { StartPlayOverlay } from "@/components/timeline-player/start-play-overlay"
import { VideoShellOverlay } from "@/components/timeline-player/video-shell-overlay"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { useShotLeftColumnHost } from "@/components/shot-panel/shot-left-column-host"
import { CorrectionPromptDialog } from "@/components/workspace/correction-prompt-dialog"
import { Button, Kbd } from "@/components/ui/primitives"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import type { PipSources } from "@/lib/player-pip"
import { studioTimelineBundleFromEditor, type StudioTimelineBundle } from "@/lib/studio-sequence"
import { cn } from "@/lib/utils"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"
import type { VoiceSelection } from "@/lib/voices-api"
import { usePointerIdle } from "@/hooks/use-pointer-idle"
import { useShellFullscreen } from "@/hooks/use-shell-fullscreen"
import { useTranslation } from "@/hooks/use-translation"
import { getCsrfToken, fetchShotTimelineVoices } from "@/lib/workspace-api"
import { AddTrackMenu } from "./add-track-menu"
import {
  AssetGoneError,
  createShotAudioAsset,
  createShotAudioAssetTts,
  destroyShotAudioAsset,
  fetchShotAudioAsset,
  patchShotTimeline,
  postShotTimelineLipsync,
  renameShotMediaVersion,
  renderShotTimeline,
  StaleTimelineConflictError,
} from "./api"
import { AudioRecordDialog } from "./audio-record-dialog"
import type { TakeVideoSync } from "./audio-take-timeline"
import { AudioTtsDialog } from "./audio-tts-dialog"
import { ClipSpeedControl } from "./clip-speed-control"
import {
  addAudioAssetClip,
  addCorrectionTrack,
  addCustomAudioTrack,
  addCustomAudioTrackWithAsset,
  insertAudioAssetClipAtStart,
  addAnimaticTrack,
  addVideoTrack,
  bladeAt,
  clipDuration,
  clipSpeed,
  clipVersionId,
  clampCorrectionSourceWindow,
  collectSnapPoints,
  CORRECTION_MIN_CLIP_MS,
  correctionSourceEligible,
  detachAudio,
  dropStaleAnimaticTracks,
  emptyDocument,
  findClip,
  findTrack,
  isAnimaticTrack,
  repairDetachedAudio,
  moveClip,
  moveTrackToIndex,
  rateStretchClip,
  removeClip,
  trackGroupRange,
  removeTrack,
  resizeClip,
  setClipFade,
  setClipSpeed,
  setTrackHidden,
  setTrackMuted,
  setTrackVolume,
  snapEdge,
  snapMoveStart,
  timelineDuration,
  videoTrackCount,
} from "./timeline-model"
import { TrackHeader } from "./track-header"
import { TrackLane } from "./track-lane"
import type {
  ShotTimelineEditorData,
  TimelineAudioAsset,
  TimelineClip,
  TimelineDocument,
  TimelineLibraryVoiceRef,
  TimelineSource,
  TimelineVoiceOption,
} from "./types"
import {
  readPlayerAudioScrubPref,
  readPlayerLoopPref,
  useTimelinePlayback,
  writePlayerAudioScrubPref,
  writePlayerLoopPref,
  type EngineSources,
  type PlaybackTimeStore,
  type TimelinePlaybackControls,
  type AnimaticMode,
} from "@/lib/timeline"
import type { GenerationJobRow } from "@/types/workspace"

const SHORTCUT_SECTIONS = [
  {
    titleKey: "player.shortcutsPlayback",
    rows: [
      { keys: [["Space"]], labelKey: "player.shortcutPlayPause" },
      { keys: [["J"]], labelKey: "player.shortcutReverse" },
      { keys: [["K"]], labelKey: "player.shortcutPause" },
      { keys: [["L"]], labelKey: "player.shortcutPlayForward" },
    ],
  },
  {
    titleKey: "player.shortcutsSeek",
    rows: [
      { keys: [["←"]], labelKey: "player.shortcutFrameBack" },
      { keys: [["→"]], labelKey: "player.shortcutFrameForward" },
      { keys: [["Shift", "←"]], labelKey: "player.shortcutSeekBack" },
      { keys: [["Shift", "→"]], labelKey: "player.shortcutSeekForward" },
      { keys: [["Home"]], labelKey: "player.shortcutToStart" },
      { keys: [["End"]], labelKey: "player.shortcutToEnd" },
    ],
  },
  {
    titleKey: "player.shortcutsEdit",
    rows: [
      { keys: [["B"]], labelKey: "timeline.shortcutBlade" },
      { keys: [["Delete"], ["Backspace"]], labelKey: "timeline.shortcutDeleteClip" },
      { keys: [["Alt", "Drag"]], labelKey: "timeline.shortcutRateStretch" },
    ],
  },
  {
    titleKey: "player.shortcutsView",
    rows: [
      {
        keys: [
          ["⌘", "Scroll"],
          ["Ctrl", "Scroll"],
        ],
        labelKey: "timeline.shortcutZoom",
      },
    ],
  },
] as const

/** Таймкод HH:MM:SS:FF в стиле NLE. */
function formatTimecode(ms: number, fps: number): string {
  const safeMs = Math.max(0, ms)
  const totalSec = Math.floor(safeMs / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const f = Math.floor(((safeMs % 1000) / 1000) * fps)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(h)}:${pad(m)}:${pad(s)}:${pad(f)}`
}

/** Ширина липкой колонки шапок: 136px шапка + 4px зазор до дорожки. */
const HEADER_W = 140
/** Шаг ряда дорожек при drag-and-drop: высота дорожки h-12 (48px) + gap-1 (4px). */
const TRACK_ROW_STEP_PX = 52
const ASSET_POLL_MS = 1500
/** Порог прилипания краёв клипов (px на экране, не зависит от зума). */
const SNAP_PX = 8

/** Пределы зума таймлайна. */
const MIN_PX_PER_MS = 0.005
const MAX_PX_PER_MS = 1
/** Шаги major-делений линейки (секунды) — берётся первый с расстоянием между подписями >= 50px. */
const RULER_STEPS_S = [0.1, 0.2, 0.5, 1, 2, 5, 10, 30, 60, 120, 300, 600]
/** Деления major-интервала на minor-насечки (от самого плотного к редкому). */
const RULER_MINOR_DIVISIONS = [10, 5, 4, 2]
const RULER_MAJOR_MIN_PX = 50
const RULER_MINOR_MIN_PX = 8

/** План делений линейки: major с подписью + minor без подписи (плотность от зума). */
function rulerTickPlan(pxPerMs: number): { majorStepS: number; divisions: number; minorStepMs: number } {
  const majorStepS =
    RULER_STEPS_S.find((s) => s * 1000 * pxPerMs >= RULER_MAJOR_MIN_PX) ?? RULER_STEPS_S[RULER_STEPS_S.length - 1]
  const majorSpacingPx = majorStepS * 1000 * pxPerMs
  const divisions = RULER_MINOR_DIVISIONS.find((d) => majorSpacingPx / d >= RULER_MINOR_MIN_PX) ?? 1
  return { majorStepS, divisions, minorStepMs: (majorStepS * 1000) / divisions }
}

/** Подпись major-деления: целые секунды без дроби, доли — с одной цифрой после точки. */
function formatRulerLabel(seconds: number): string {
  const rounded = Math.round(seconds * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1)
}

/**
 * Таймкод и плейхед обновляются императивно (подписка на store + прямые записи
 * в DOM через ref): во время воспроизведения React не делает ни одного коммита.
 */
function TimecodeDisplay({
  store,
  fps,
  durationMs,
  title,
}: {
  store: PlaybackTimeStore
  fps: number
  durationMs: number
  title: string
}) {
  const currentRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    let lastText = ""
    const update = () => {
      const text = formatTimecode(store.get(), fps)
      // Текст меняется только на границе кадра — пишем в DOM реже, чем тикает rAF.
      if (text === lastText) return
      lastText = text
      if (currentRef.current) currentRef.current.textContent = text
    }
    update()
    return store.subscribe(update)
  }, [store, fps])

  return (
    <span className="font-mono text-[11px] text-ui-text-secondary tabular-nums" title={title}>
      <span ref={currentRef} />
      <span className="mx-1 text-ui-text-muted">/</span>
      {formatTimecode(durationMs, fps)}
    </span>
  )
}

function Playhead({
  store,
  pxPerMs,
  offsetPx,
  playingRef,
  scrollRef,
}: {
  store: PlaybackTimeStore
  pxPerMs: number
  offsetPx: number
  playingRef: React.RefObject<boolean>
  scrollRef: React.RefObject<HTMLDivElement | null>
}) {
  const lineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const update = () => {
      const x = offsetPx + store.get() * pxPerMs
      if (lineRef.current) lineRef.current.style.transform = `translateX(${x}px)`

      // Во время воспроизведения подскролливаем контейнер, если плейхед ушёл из видимой области.
      if (!playingRef.current) return
      const el = scrollRef.current
      if (!el || el.scrollWidth <= el.clientWidth) return
      const visibleLeft = el.scrollLeft + offsetPx
      const visibleRight = el.scrollLeft + el.clientWidth - 16
      if (x < visibleLeft || x > visibleRight) {
        el.scrollLeft = Math.max(0, x - offsetPx)
      }
    }
    update()
    return store.subscribe(update)
  }, [store, pxPerMs, offsetPx, playingRef, scrollRef])

  return (
    <div
      ref={lineRef}
      className="pointer-events-none absolute top-0 bottom-0 left-0 z-30 w-px bg-ui-accent shadow-sm shadow-violet-400/50"
    />
  )
}

type Props = {
  projectId: number
  shotId: number
  data: ShotTimelineEditorData
  /** Вкладка «Видео» активна: включает шорткаты; при false плеер ставится на паузу. */
  active: boolean
  /** Идёт job рендера таймлайна — блокирует кнопку рендера. */
  renderJobActive: boolean
  /** Активные lipsync-джобы шота — их клипы замораживаются на дорожках. */
  lipsyncJobs: GenerationJobRow[]
  /** In-flight video correction jobs with correction_clip_id — lock that clip. */
  correctionJobs?: GenerationJobRow[]
  onRenderStarted: () => void
  /**
   * Колбэк с controls для внешних действий над плеером (захват кадра и т.п.),
   * по образцу onPlaybackReady у TimelinePlayer. При unmount вызывается с null.
   */
  onPlaybackReady?: (controls: TimelinePlaybackControls | null) => void
  /** Плейхед таймлайна шота (мс). При unmount — null. */
  onTimeStore?: (store: PlaybackTimeStore | null) => void
  /** Шот доиграл вперёд и цикл выключен. Пауза этот колбэк не вызывает. */
  onEnded?: () => void
  /** Актуальный документ и URL источников. Студия кладёт его в простой просмотр. */
  onDocumentChange?: (shotId: number, bundle: StudioTimelineBundle) => void
  /** Кнопка «Скриншот» в тулбаре: сам захват делает владелец — ему принадлежит список скриншотов. */
  captureAction?: { onCapture: () => void; busy: boolean; disabled: boolean; label: string }
  videoCorrectionEnabled?: boolean
  videoCorrectionAdvanced?: boolean
  videoCorrectionTrimMaxSeconds?: number
  videoCorrectionMarkupMax?: number
  onCorrectionQueued?: () => void
  /** Студия: кадр занимает оставшуюся высоту колонки, дорожки снизу со своим скроллом. */
  layout?: "default" | "studio"
  /** Слот сразу после кнопки цикла, до аудио-скраба. */
  afterLoop?: ReactNode
  /** Старт цикла. Без пропа — общий преф плеера. Преф при этом не пишется. */
  defaultLoop?: boolean
  /** Один раз запустить воспроизведение после setLoop. */
  autoPlay?: boolean
  /** Превиз и аниматик текущего шота. null — кнопки картинки в картинке нет. */
  pipSources?: PipSources | null
  /** Метка в левом нижнем углу кадра. Клики не перехватывает. */
  videoCorner?: ReactNode
}

type ClipSelection = { trackId: string; clipId: string }

const EMPTY_SELECTED_IDS: readonly string[] = []

/** Мультитрек-редактор видео шота: превью, транспорт, дорожки, лезвие, аудио и рендер. */
export function TimelineEditor({
  projectId,
  shotId,
  data,
  active,
  renderJobActive,
  lipsyncJobs,
  correctionJobs = [],
  onRenderStarted,
  onPlaybackReady,
  onTimeStore,
  onEnded,
  onDocumentChange,
  captureAction,
  videoCorrectionEnabled = false,
  videoCorrectionAdvanced = false,
  videoCorrectionTrimMaxSeconds = 15,
  videoCorrectionMarkupMax = 0,
  onCorrectionQueued,
  layout = "default",
  afterLoop,
  defaultLoop,
  autoPlay = false,
  pipSources = null,
  videoCorner = null,
}: Props) {
  const { t } = useTranslation(["shot", "common"])
  const studioLayout = layout === "studio"
  const fps = data.frame_rate > 0 ? data.frame_rate : DEFAULT_VIDEO_OUTPUT_FPS
  const frameMs = 1000 / fps
  const shellAspectRatio = data.aspect_ratio != null && data.aspect_ratio > 0 ? data.aspect_ratio : 16 / 9

  const leftColumnHost = useShotLeftColumnHost()
  const rootRef = useRef<HTMLDivElement>(null)
  const [editorRoot, setEditorRoot] = useState<HTMLDivElement | null>(null)
  const setEditorRootEl = useCallback((node: HTMLDivElement | null) => {
    rootRef.current = node
    setEditorRoot(node)
  }, [])
  const composePortalTarget = leftColumnHost ?? (studioLayout ? editorRoot : null)
  const shellRef = useRef<HTMLDivElement>(null)
  const lanesRef = useRef<HTMLDivElement>(null)
  const rulerRef = useRef<HTMLDivElement>(null)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const uploadTrackIdRef = useRef<string | null>(null)

  const [doc, setDoc] = useState<TimelineDocument>(() => {
    const raw =
      data.timeline && "tracks" in data.timeline && Array.isArray(data.timeline.tracks)
        ? // Ремонт «застрявшего» detached-видео без пары: сервер чинит только при
          // сохранении, а открыть такой документ надо уже с доступным контролом звука.
          repairDetachedAudio(data.timeline as TimelineDocument)
        : emptyDocument()
    return dropStaleAnimaticTracks(raw, data.animatic?.segment_id ?? null)
  })
  const [assets, setAssets] = useState<TimelineAudioAsset[]>(data.audio_assets)
  const [versions, setVersions] = useState(data.versions)
  // Voices are lazy: detail payload leaves them empty; fill after mount for VoicePicker/TTS.
  const [voices, setVoices] = useState<TimelineVoiceOption[]>(data.voices)
  const [defaultLibraryVoice, setDefaultLibraryVoice] = useState<TimelineLibraryVoiceRef | null>(
    data.default_library_voice,
  )
  const [ttsProviderKind, setTtsProviderKind] = useState<string | null>(data.tts_provider_kind ?? null)
  const [stsProviderKinds, setStsProviderKinds] = useState<string[]>(data.sts_provider_kinds)
  // Множественное выделение: обычный клик — замена, cmd/ctrl+клик — toggle.
  // Primary (последний выбранный) используется одноклиповыми действиями (blade, speed).
  const [selected, setSelected] = useState<ClipSelection[]>([])
  const [loop, setLoop] = useState(() => defaultLoop ?? readPlayerLoopPref())
  const [audioScrub, setAudioScrub] = useState(() => readPlayerAudioScrubPref())
  const [snapEnabled, setSnapEnabled] = useState(true)
  const [muted, setMuted] = useState(false)
  /** Синхронный дубль: шот молчит, кнопка mute не трогается. */
  const [dubSilent, setDubSilent] = useState(false)
  const [volume, setVolume] = useState(1)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [composeSession, setComposeSession] = useState<{ trackId: string; mode: "record" | "tts" } | null>(null)
  const [assetSubmitting, setAssetSubmitting] = useState(false)
  const [renderSubmitting, setRenderSubmitting] = useState(false)
  const [lipsyncSubmitting, setLipsyncSubmitting] = useState(false)
  const [laneWidth, setLaneWidth] = useState(0)
  const [correctionTarget, setCorrectionTarget] = useState<{
    trackId: string
    clipId: string
  } | null>(null)
  /** Optimistic lock until correction job appears in correctionJobs. */
  const [pendingCorrectionClipIds, setPendingCorrectionClipIds] = useState<Set<string>>(() => new Set())

  const docRef = useRef(doc)
  docRef.current = doc
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  const snapEnabledRef = useRef(snapEnabled)
  snapEnabledRef.current = snapEnabled

  // Замороженные клипы: lipsync + video correction (job + optimistic pending).
  const lockedClipIds = useMemo(() => {
    const ids = new Set<string>()
    for (const job of lipsyncJobs) {
      for (const clipId of job.lipsync_clip_ids ?? []) ids.add(clipId)
    }
    for (const job of correctionJobs) {
      const id = job.correction_clip_id
      if (id) ids.add(id)
    }
    for (const id of pendingCorrectionClipIds) ids.add(id)
    return ids
  }, [lipsyncJobs, correctionJobs, pendingCorrectionClipIds])
  const lockedClipIdsRef = useRef(lockedClipIds)
  lockedClipIdsRef.current = lockedClipIds

  // Drop optimistic pending once the job list includes the clip.
  useEffect(() => {
    const fromJobs = new Set<string>()
    for (const job of correctionJobs) {
      if (job.correction_clip_id) fromJobs.add(job.correction_clip_id)
    }
    if (fromJobs.size === 0) return
    setPendingCorrectionClipIds((prev) => {
      if (prev.size === 0) return prev
      let changed = false
      const next = new Set<string>()
      for (const id of prev) {
        if (fromJobs.has(id)) {
          changed = true
          continue
        }
        next.add(id)
      }
      return changed ? next : prev
    })
  }, [correctionJobs])

  const markCorrectionClipPending = useCallback((clipId: string) => {
    setPendingCorrectionClipIds((prev) => {
      if (prev.has(clipId)) return prev
      const next = new Set(prev)
      next.add(clipId)
      return next
    })
  }, [])

  const clearCorrectionClipPending = useCallback((clipId: string) => {
    setPendingCorrectionClipIds((prev) => {
      if (!prev.has(clipId)) return prev
      const next = new Set(prev)
      next.delete(clipId)
      return next
    })
  }, [])

  // Снятие выделения с клипов, ставших замороженными (джоба началась).
  useEffect(() => {
    if (lockedClipIds.size === 0) return
    setSelected((prev) => {
      const next = prev.filter((sel) => !lockedClipIds.has(sel.clipId))
      return next.length === prev.length ? prev : next
    })
  }, [lockedClipIds])
  // Дефолтный (не сохранённый) таймлайн: не PATCH'им, пока пользователь не отредактирует.
  const dirtyRef = useRef(!data.is_default)
  const savedDocRef = useRef<TimelineDocument>(doc)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const versionDurationsRef = useRef<Map<number, number>>(new Map())
  /** Reactive copy of probed/server durations (ref alone does not re-render AddTrackMenu). */
  const [probedDurationsMs, setProbedDurationsMs] = useState<Record<number, number>>({})
  const assetPollsRef = useRef<Map<number, number>>(new Map())
  const pendingPlacementsRef = useRef<
    Map<number, { trackId: string; atMs: number; insertAtStart: boolean; maxDurationMs: number | null }>
  >(new Map())

  useEffect(() => {
    const next: Record<number, number> = {}
    for (const source of Object.values(data.sources)) {
      if (source.duration_ms != null && source.duration_ms > 0) {
        versionDurationsRef.current.set(source.version_id, source.duration_ms)
        next[source.version_id] = source.duration_ms
      }
    }
    if (Object.keys(next).length > 0) {
      setProbedDurationsMs((prev) => ({ ...prev, ...next }))
    }
  }, [data.sources])

  // Refetch шота (например, после lipsync-джобы) приносит новые версии — обновляем список.
  useEffect(() => {
    setVersions(data.versions)
  }, [data.versions])

  useEffect(() => {
    let cancelled = false
    void fetchShotTimelineVoices(projectId, shotId)
      .then((payload) => {
        if (cancelled) return
        setVoices(payload.voices)
        setDefaultLibraryVoice(payload.default_library_voice)
        setTtsProviderKind(payload.tts_provider_kind)
        setStsProviderKinds(payload.sts_provider_kinds)
      })
      .catch(() => {
        // Voice picker stays empty; playback does not depend on this.
      })
    return () => {
      cancelled = true
    }
  }, [projectId, shotId])

  // Новая генерация видео: сменился primary — подменяем локальный doc дефолтным с сервера.
  const prevPrimaryVersionIdRef = useRef(data.primary_version_id)
  useEffect(() => {
    if (prevPrimaryVersionIdRef.current === data.primary_version_id) return
    prevPrimaryVersionIdRef.current = data.primary_version_id
    if (!data.is_default) return
    const serverTimeline =
      data.timeline && "tracks" in data.timeline && Array.isArray(data.timeline.tracks)
        ? (data.timeline as TimelineDocument)
        : null
    if (!serverTimeline) return
    const cleaned = dropStaleAnimaticTracks(serverTimeline, data.animatic?.segment_id ?? null)
    dirtyRef.current = false
    savedDocRef.current = cleaned
    docRef.current = cleaned
    setDoc(cleaned)
    setSelected([])
  }, [data.is_default, data.primary_version_id, data.timeline, data.animatic?.segment_id])

  // После завершения lipsync refetch приносит клипы с override версии: подменяем их
  // по id в локальном документе, не трогая остальные локальные правки пользователя.
  useEffect(() => {
    const serverTimeline =
      data.timeline && "tracks" in data.timeline && Array.isArray(data.timeline.tracks)
        ? (data.timeline as TimelineDocument)
        : null
    if (!serverTimeline) return
    const overrides = new Map<string, TimelineClip>()
    for (const track of serverTimeline.tracks) {
      if (track.kind === "custom_audio") continue
      for (const clip of track.clips) {
        if (clip.shot_media_version_id != null) overrides.set(clip.id, clip)
      }
    }
    if (overrides.size === 0) return

    const mergeOverrides = (docToMerge: TimelineDocument): TimelineDocument | null => {
      let changed = false
      const tracks = docToMerge.tracks.map((track) => {
        if (track.kind === "custom_audio") return track
        let trackChanged = false
        const clips = track.clips.map((clip) => {
          const server = overrides.get(clip.id)
          if (!server || clip.shot_media_version_id === server.shot_media_version_id) return clip
          trackChanged = true
          return { ...server }
        })
        if (!trackChanged) return track
        changed = true
        return { ...track, clips }
      })
      return changed ? { ...docToMerge, tracks } : null
    }

    const nextDoc = mergeOverrides(docRef.current)
    if (nextDoc) {
      docRef.current = nextDoc
      setDoc(nextDoc)
    }
    // Слитый клип уже сохранён на сервере — обновляем и rollback-базу.
    const nextSaved = mergeOverrides(savedDocRef.current)
    if (nextSaved) savedDocRef.current = nextSaved
  }, [data.timeline])

  // ── Сохранение: оптимистично + debounce, откат при ошибке ──
  const doSave = useCallback(
    async (next: TimelineDocument) => {
      try {
        const normalized = await patchShotTimeline(projectId, shotId, next, data.primary_version_id)
        savedDocRef.current = normalized ?? next
      } catch (err) {
        // Активная версия уже сменилась (генерация / activate) — не тостом и не откатом
        // отравлять UI: редактор ремаунтится с новыми данными.
        if (err instanceof StaleTimelineConflictError) return
        toast.error(err instanceof Error ? err.message : t("timeline.saveFailed"))
        setDoc(savedDocRef.current)
      }
    },
    [projectId, shotId, data.primary_version_id, t],
  )
  const doSaveRef = useRef(doSave)
  doSaveRef.current = doSave

  const applyDoc = useCallback((next: TimelineDocument) => {
    dirtyRef.current = true
    setDoc(next)
    docRef.current = next
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null
      void doSaveRef.current(docRef.current)
    }, 500)
  }, [])

  const linkedAnimaticSegmentId = data.animatic?.segment_id ?? null
  useEffect(() => {
    const next = dropStaleAnimaticTracks(docRef.current, linkedAnimaticSegmentId)
    if (next === docRef.current) return
    applyDoc(next)
    setSelected((prev) => prev.filter((sel) => next.tracks.some((tr) => tr.id === sel.trackId)))
  }, [linkedAnimaticSegmentId, applyDoc])

  const displayDoc = useMemo(
    () => dropStaleAnimaticTracks(doc, linkedAnimaticSegmentId),
    [doc, linkedAnimaticSegmentId],
  )

  useEffect(
    () => () => {
      // Не терять отложенный PATCH при закрытии панели.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
        void doSaveRef.current(docRef.current)
      }
    },
    [],
  )

  // ── Воспроизведение ──
  const engineSources = useMemo<EngineSources>(() => {
    const versionUrls: Record<number, string> = {}
    const scrubUrls: Record<number, string> = {}
    const captureUrls: Record<number, string> = {}
    const scrubDurationsMs: Record<string, number> = {}
    for (const source of Object.values(data.sources)) {
      if (source.stream_url) versionUrls[source.version_id] = source.stream_url
      if (source.scrub_url && source.duration_ms) {
        scrubUrls[source.version_id] = source.scrub_url
        scrubDurationsMs[source.scrub_url] = source.duration_ms
      }
      if (source.capture_url) captureUrls[source.version_id] = source.capture_url
    }
    const assetUrls: Record<number, string> = {}
    for (const asset of assets) {
      if (!asset.url) continue
      assetUrls[asset.id] = asset.url
      // Без scrub_ok (серверный байт-гейт) источник не фетчится ради скраба вовсе:
      // отсутствие длительности в карте — и есть пре-гейт декода.
      if (asset.duration_ms && asset.scrub_ok) scrubDurationsMs[asset.url] = asset.duration_ms
    }
    const animatic = data.animatic
    if (animatic?.scrub_url && animatic.duration_ms) {
      scrubDurationsMs[animatic.scrub_url] = animatic.duration_ms
    }
    return {
      versionUrls,
      assetUrls,
      scrubUrls,
      captureUrls,
      scrubDurationsMs,
      animaticUrl: animatic?.stream_url ?? null,
      animaticScrubUrl: animatic?.scrub_url ?? null,
    }
  }, [data.sources, data.animatic, assets])

  const onDocumentChangeRef = useRef(onDocumentChange)
  onDocumentChangeRef.current = onDocumentChange
  useEffect(() => {
    if (!onDocumentChangeRef.current) return
    const publish = () => {
      const onDocumentChange = onDocumentChangeRef.current
      if (!onDocumentChange) return
      onDocumentChange(
        shotId,
        studioTimelineBundleFromEditor({
          timeline: doc,
          versionUrls: engineSources.versionUrls,
          assetUrls: engineSources.assetUrls,
          animaticSegmentId: data.animatic?.segment_id ?? null,
          animaticUrl: engineSources.animaticUrl,
        }),
      )
    }
    publish()
    return publish
  }, [doc, shotId, engineSources, data.animatic?.segment_id])

  const animaticTimelineSource = useMemo<TimelineSource | null>(() => {
    const a = data.animatic
    if (!a?.playable || !a.stream_url) return null
    return {
      version_id: a.segment_id,
      stream_url: a.stream_url,
      scrub_url: a.scrub_url,
      duration_ms: a.duration_ms,
      frame_rate: a.frame_rate,
      aspect_ratio: a.aspect_ratio,
      timeline_frame_urls: a.timeline_frame_urls,
    }
  }, [data.animatic])

  const handleVersionDuration = useCallback((versionId: number, durationMs: number) => {
    versionDurationsRef.current.set(versionId, durationMs)
    setProbedDurationsMs((prev) => (prev[versionId] === durationMs ? prev : { ...prev, [versionId]: durationMs }))
    const current = docRef.current
    let changed = false
    const tracks = current.tracks.map((track) => {
      if (track.kind === "custom_audio") return track
      const clips = track.clips.map((clip) => {
        if (clipVersionId(track, clip) !== versionId) return clip
        // Дефолтный полный клип с fallback-длиной растягиваем до реальной; выходы за конец клампим.
        const isFullDefault = !dirtyRef.current && clip.source_in_ms === 0 && track.clips.length === 1
        if (isFullDefault && clip.source_out_ms !== durationMs) {
          changed = true
          return { ...clip, source_out_ms: durationMs }
        }
        if (clip.source_out_ms > durationMs) {
          changed = true
          return { ...clip, source_out_ms: Math.max(durationMs, clip.source_in_ms + 1) }
        }
        return clip
      })
      return { ...track, clips }
    })
    if (!changed) return
    const next = { ...current, tracks }
    // Коррекция метаданных не считается пользовательской правкой — дефолт не сохраняем.
    docRef.current = next
    setDoc(next)
  }, [])

  // Тумблер слышимого скраба показываем, только когда движку реально есть что фетчить.
  const hasScrubSources =
    Object.keys(engineSources.assetUrls).length > 0 ||
    Object.keys(engineSources.scrubUrls ?? {}).length > 0 ||
    Boolean(engineSources.animaticScrubUrl)

  const playback = useTimelinePlayback({
    doc: displayDoc,
    sources: engineSources,
    onVersionDuration: handleVersionDuration,
    onEnded,
  })
  const { controls, timeStore, playing, buffering } = playback
  const shotDubSync = useMemo<TakeVideoSync>(
    () => ({
      // На конце play() сам прыгает в 0. Для подтяжки дрейфа это перезапуск шота посреди дубля.
      play: () => {
        const end = timelineDuration(docRef.current)
        if (end > 0 && controls.currentMs() >= end - 1) return
        controls.play(1)
      },
      pause: () => controls.pause(),
      seek: (ms) => controls.seek(ms),
      scrubTo: (ms) => controls.scrubTo(ms),
      endScrub: () => controls.endScrub(),
      currentMs: () => controls.currentMs(),
      isPlaying: () => controls.isPlaying(),
    }),
    [controls],
  )
  const playingRef = useRef(playing)
  playingRef.current = playing
  const { forwardRate, pauseAll, togglePlay, stepFrames, stepMs, goToStart, goToEnd, playForward, playReverse } =
    usePlaybackTransport({
      controls,
      playing,
      frameMs,
      getEndMs: () => timelineDuration(docRef.current),
      active,
    })

  useEffect(() => {
    if (!onPlaybackReady) return
    onPlaybackReady(controls)
    return () => onPlaybackReady(null)
  }, [onPlaybackReady, controls])

  useEffect(() => {
    if (!onTimeStore) return
    onTimeStore(timeStore)
    return () => onTimeStore(null)
  }, [onTimeStore, timeStore])

  const { isFullscreen, isPseudoFullscreen, toggleFullscreen } = useShellFullscreen(
    shellRef,
    controls.enterNativeVideoFullscreen,
  )
  // Автоскрытие контролов в фулскрине: только во время воспроизведения; на паузе видны.
  const {
    active: pointerActive,
    onControlsPointerEnter,
    onControlsPointerLeave,
    wasRevealTap,
  } = usePointerIdle({ containerRef: shellRef, enabled: isFullscreen && playing })
  const controlsVisible = !isFullscreen || pointerActive

  useEffect(() => {
    controls.setLoop(loop)
  }, [controls, loop])
  useEffect(() => {
    if (!autoPlay || !active) return
    let cancelled = false
    let attempts = 0
    const tick = () => {
      if (cancelled) return
      controls.play(1)
      if (controls.isPlaying()) return
      attempts += 1
      if (attempts < 20) window.setTimeout(tick, 50)
    }
    tick()
    return () => {
      cancelled = true
    }
  }, [autoPlay, active, controls])
  useEffect(() => {
    controls.setAudioScrub(audioScrub)
  }, [controls, audioScrub])
  useEffect(() => {
    controls.setMasterMuted(muted || dubSilent)
  }, [controls, muted, dubSilent])
  useEffect(() => {
    // Закрытие модалки и смена дорожки возвращают звук кнопки mute.
    // Тишину дубля заново включает onPlayForDub, deps этого эффекта при этом не меняются.
    setDubSilent(false)
  }, [composeSession?.mode, composeSession?.trackId])
  useEffect(() => {
    controls.setMasterVolume(volume)
  }, [controls, volume])

  const durationMs = useMemo(() => timelineDuration(displayDoc), [displayDoc])

  const handleViewportClick = useCallback(() => {
    // Фулскрин на таче: тап по скрытым контролам только показывает их, а не ставит паузу.
    if (isFullscreen && wasRevealTap()) return
    togglePlay()
  }, [isFullscreen, wasRevealTap, togglePlay])

  // ── Операции редактирования ──
  const handleBlade = useCallback(() => {
    const at = controls.currentMs()
    const sels = selectedRef.current
    const primary = sels.length > 0 ? sels[sels.length - 1] : undefined
    applyDoc(bladeAt(docRef.current, at, primary, lockedClipIdsRef.current))
  }, [controls, applyDoc])

  const handleDeleteSelected = useCallback(() => {
    const sels = selectedRef.current.filter((sel) => !lockedClipIdsRef.current.has(sel.clipId))
    if (sels.length === 0) return
    let next = docRef.current
    for (const sel of sels) next = removeClip(next, sel.trackId, sel.clipId)
    applyDoc(next)
    setSelected([])
  }, [applyDoc])

  const clipSourceDuration = useCallback(
    (trackId: string, clipId: string): number | null => {
      const track = docRef.current.tracks.find((tr) => tr.id === trackId)
      if (!track) return null
      if (isAnimaticTrack(track)) return data.animatic?.duration_ms ?? null
      const clip = track.clips.find((c) => c.id === clipId)
      if (!clip) return null
      if (track.kind === "custom_audio") {
        if (clip.audio_asset_id == null) return null
        return assets.find((a) => a.id === clip.audio_asset_id)?.duration_ms ?? null
      }
      const versionId = clipVersionId(track, clip)
      if (versionId == null) return null
      return versionDurationsRef.current.get(versionId) ?? null
    },
    [assets, data.animatic],
  )

  // ── Стабильные обработчики дорожек (для React.memo на TrackHeader/TrackLane) ──
  const handleSelectClip = useCallback((trackId: string, clipId: string, additive: boolean) => {
    if (lockedClipIdsRef.current.has(clipId)) return
    setSelected((prev) => {
      if (!additive) return [{ trackId, clipId }]
      const exists = prev.some((sel) => sel.clipId === clipId)
      if (exists) return prev.filter((sel) => sel.clipId !== clipId)
      return [...prev, { trackId, clipId }]
    })
  }, [])

  const snapToleranceMs = useCallback(() => {
    const pxPerMs = pxPerMsRef.current
    return snapEnabledRef.current && pxPerMs > 0 ? SNAP_PX / pxPerMs : 0
  }, [])

  const handleMoveClip = useCallback(
    (trackId: string, clipId: string, newStartMs: number) => {
      const track = docRef.current.tracks.find((tr) => tr.id === trackId)
      if (track?.purpose === "correction") return
      let startMs = newStartMs
      const toleranceMs = snapToleranceMs()
      if (toleranceMs > 0) {
        const clip = track?.clips.find((c) => c.id === clipId)
        if (clip) {
          const points = collectSnapPoints(docRef.current, trackId, clipId, controls.currentMs())
          startMs = snapMoveStart(newStartMs, clipDuration(clip), points, toleranceMs)
        }
      }
      applyDoc(moveClip(docRef.current, trackId, clipId, startMs))
    },
    [applyDoc, snapToleranceMs, controls],
  )

  const handleResizeClip = useCallback(
    (trackId: string, clipId: string, edge: "left" | "right", newEdgeMs: number) => {
      const track = docRef.current.tracks.find((tr) => tr.id === trackId)
      const sourceDuration = clipSourceDuration(trackId, clipId)
      let edgeMs = newEdgeMs
      const toleranceMs = snapToleranceMs()
      if (toleranceMs > 0) {
        const points = collectSnapPoints(docRef.current, trackId, clipId, controls.currentMs())
        edgeMs = snapEdge(newEdgeMs, points, toleranceMs)
      }
      let next = resizeClip(docRef.current, trackId, clipId, edge, edgeMs, sourceDuration)
      if (track?.purpose === "correction") {
        const maxWindowMs = videoCorrectionAdvanced ? Math.round(videoCorrectionTrimMaxSeconds * 1000) : null
        next = clampCorrectionSourceWindow(next, trackId, clipId, edge, CORRECTION_MIN_CLIP_MS, maxWindowMs)
      }
      applyDoc(next)
    },
    [applyDoc, clipSourceDuration, snapToleranceMs, controls, videoCorrectionAdvanced, videoCorrectionTrimMaxSeconds],
  )

  const handleRateStretchClip = useCallback(
    (trackId: string, clipId: string, edge: "left" | "right", newEdgeMs: number) => {
      let edgeMs = newEdgeMs
      const toleranceMs = snapToleranceMs()
      if (toleranceMs > 0) {
        const points = collectSnapPoints(docRef.current, trackId, clipId, controls.currentMs())
        edgeMs = snapEdge(newEdgeMs, points, toleranceMs)
      }
      applyDoc(rateStretchClip(docRef.current, trackId, clipId, edge, edgeMs))
    },
    [applyDoc, snapToleranceMs, controls],
  )

  const handleSetClipSpeed = useCallback(
    (speed: number) => {
      const sels = selectedRef.current
      const sel = sels.length > 0 ? sels[sels.length - 1] : null
      if (!sel || lockedClipIdsRef.current.has(sel.clipId)) return
      applyDoc(setClipSpeed(docRef.current, sel.trackId, sel.clipId, speed))
    },
    [applyDoc],
  )

  const handleFadeClip = useCallback(
    (trackId: string, clipId: string, edge: "in" | "out", fadeMs: number) =>
      applyDoc(setClipFade(docRef.current, trackId, clipId, edge, fadeMs)),
    [applyDoc],
  )

  const handleClipDragEnd = useCallback(() => {}, [])

  const handleTrackAudioChange = useCallback(
    (trackId: string, volume: number, muted: boolean) =>
      applyDoc(setTrackMuted(setTrackVolume(docRef.current, trackId, volume), trackId, muted)),
    [applyDoc],
  )

  const handleToggleTrackHidden = useCallback(
    (trackId: string) => {
      const track = docRef.current.tracks.find((tr) => tr.id === trackId)
      if (!track) return
      applyDoc(setTrackHidden(docRef.current, trackId, track.hidden !== true))
    },
    [applyDoc],
  )

  // --- Drag-and-drop дорожек за шапку ---

  const [trackDrag, setTrackDrag] = useState<{
    trackId: string
    startY: number
    dy: number
    /** Активируется после порога 4px, чтобы не мешать кликам по кнопкам шапки. */
    active: boolean
  } | null>(null)

  const handleTrackDragDown = useCallback((trackId: string, e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    // Кнопки/инпуты шапки работают как обычно — drag начинается с остальной области.
    if ((e.target as HTMLElement).closest("button, input, select")) return
    e.currentTarget.setPointerCapture(e.pointerId)
    setTrackDrag({ trackId, startY: e.clientY, dy: 0, active: false })
  }, [])

  const handleTrackDragMove = useCallback(
    (trackId: string, e: React.PointerEvent<HTMLDivElement>) => {
      if (!trackDrag || trackDrag.trackId !== trackId) return
      const dy = e.clientY - trackDrag.startY
      setTrackDrag({ ...trackDrag, dy, active: trackDrag.active || Math.abs(dy) > 4 })
    },
    [trackDrag],
  )

  const handleTrackDragUp = useCallback(
    (trackId: string, e: React.PointerEvent<HTMLDivElement>) => {
      if (!trackDrag || trackDrag.trackId !== trackId) return
      e.currentTarget.releasePointerCapture(e.pointerId)
      if (trackDrag.active) {
        const fromIndex = docRef.current.tracks.findIndex((tr) => tr.id === trackId)
        if (fromIndex >= 0) {
          const offset = Math.round(trackDrag.dy / TRACK_ROW_STEP_PX)
          if (offset !== 0) applyDoc(moveTrackToIndex(docRef.current, trackId, fromIndex + offset))
        }
      }
      setTrackDrag(null)
    },
    [trackDrag, applyDoc],
  )

  // Клампим смещение границами группы дорожки для «живого» превью перестановки.
  const trackDragVisual = (() => {
    if (!trackDrag?.active) return null
    const fromIndex = displayDoc.tracks.findIndex((tr) => tr.id === trackDrag.trackId)
    const range = trackGroupRange(displayDoc, trackDrag.trackId)
    if (fromIndex < 0 || !range) return null
    const dy = Math.min(
      Math.max(trackDrag.dy, (range.min - fromIndex) * TRACK_ROW_STEP_PX),
      (range.max - fromIndex) * TRACK_ROW_STEP_PX,
    )
    return { fromIndex, toIndex: fromIndex + Math.round(dy / TRACK_ROW_STEP_PX), dy }
  })()

  const handleDetachTrackAudio = useCallback(
    (trackId: string) => applyDoc(detachAudio(docRef.current, trackId)),
    [applyDoc],
  )

  const handleDeleteTrack = useCallback(
    (trackId: string) => {
      applyDoc(removeTrack(docRef.current, trackId))
      setSelected([])
    },
    [applyDoc],
  )

  const handleRenameVersion = useCallback(
    (versionId: number, name: string) => {
      void renameShotMediaVersion(projectId, shotId, versionId, name)
        .then((updated) => {
          setVersions((prev) => prev.map((v) => (v.id === updated.id ? updated : v)))
        })
        .catch((err: unknown) => {
          toast.error(err instanceof Error ? err.message : t("timeline.renameFailed"))
        })
    },
    [projectId, shotId, t],
  )

  const handleOpenRecord = useCallback((trackId: string) => {
    setComposeSession((prev) =>
      prev?.trackId === trackId && prev.mode === "record" ? null : { trackId, mode: "record" },
    )
  }, [])

  const handleOpenTts = useCallback((trackId: string) => {
    setComposeSession((prev) => (prev?.trackId === trackId && prev.mode === "tts" ? null : { trackId, mode: "tts" }))
  }, [])

  const handleUploadForTrack = useCallback((trackId: string) => {
    uploadTrackIdRef.current = trackId
    uploadInputRef.current?.click()
  }, [])

  // ── Аудио-ассеты ──
  const stopAssetPoll = useCallback((assetId: number) => {
    const timer = assetPollsRef.current.get(assetId)
    if (timer != null) {
      window.clearInterval(timer)
      assetPollsRef.current.delete(assetId)
    }
  }, [])

  const startAssetPoll = useCallback(
    (assetId: number) => {
      stopAssetPoll(assetId)
      const timer = window.setInterval(() => {
        fetchShotAudioAsset(projectId, shotId, assetId)
          .then((asset) => {
            if (asset.status === "processing") return
            stopAssetPoll(assetId)
            const placement = pendingPlacementsRef.current.get(assetId)
            pendingPlacementsRef.current.delete(assetId)
            if (asset.status === "failed") {
              // Обычная запись/загрузка (TTS/STS сервер удаляет сам, сюда не попадают):
              // ошибка тостом, ассет без применения — подчищаем.
              toast.error(asset.error_message ?? t("timeline.assetFailed"))
              setAssets((prev) => prev.filter((a) => a.id !== assetId))
              void destroyShotAudioAsset(projectId, shotId, assetId).catch(() => {})
              return
            }
            setAssets((prev) => prev.map((a) => (a.id === asset.id ? asset : a)))
            if (placement && asset.duration_ms != null) {
              // Хвост файла после остановки шота на дорожку не кладём.
              const durationMs =
                placement.maxDurationMs != null
                  ? Math.min(asset.duration_ms, placement.maxDurationMs)
                  : asset.duration_ms
              applyDoc(
                placement.insertAtStart
                  ? insertAudioAssetClipAtStart(docRef.current, placement.trackId, asset.id, durationMs)
                  : addAudioAssetClip(docRef.current, placement.trackId, asset.id, placement.atMs, durationMs),
              )
            }
          })
          .catch((err) => {
            if (err instanceof AssetGoneError) {
              // TTS/STS упал: сервер удалил ассет, ошибка — в панели «Задачи»
              // (тост покажет общий jobs-lifecycle) — здесь просто прибираемся.
              stopAssetPoll(assetId)
              pendingPlacementsRef.current.delete(assetId)
              setAssets((prev) => prev.filter((a) => a.id !== assetId))
              return
            }
            // Транзиентные ошибки поллинга — продолжаем.
          })
      }, ASSET_POLL_MS)
      assetPollsRef.current.set(assetId, timer)
    },
    [projectId, shotId, stopAssetPoll, applyDoc, t],
  )

  // Failed-ассеты больше не отображаются (ошибки TTS/STS — в панели «Задачи»):
  // прилетевшие с payload остатки тихо подчищаем.
  useEffect(() => {
    const failed = assets.filter((a) => a.status === "failed")
    if (failed.length === 0) return
    setAssets((prev) => prev.filter((a) => a.status !== "failed"))
    for (const asset of failed) {
      void destroyShotAudioAsset(projectId, shotId, asset.id).catch(() => {})
    }
  }, [assets, projectId, shotId])

  // Поллинг всех processing-ассетов (включая оставшиеся после перезагрузки страницы).
  useEffect(() => {
    const polls = assetPollsRef.current
    for (const asset of assets) {
      if (asset.status === "processing" && !polls.has(asset.id)) startAssetPoll(asset.id)
    }
    for (const [id, timer] of polls) {
      if (!assets.some((a) => a.id === id && a.status === "processing")) {
        window.clearInterval(timer)
        polls.delete(id)
      }
    }
  }, [assets, startAssetPoll])

  useEffect(() => {
    const polls = assetPollsRef.current
    return () => {
      for (const timer of polls.values()) window.clearInterval(timer)
      polls.clear()
    }
  }, [])

  const submitAssetFile = useCallback(
    async (
      trackId: string,
      file: File,
      kind: "upload" | "recording",
      voice: VoiceSelection | null = null,
      placement: { atMs?: number; insertAtStart?: boolean; maxDurationMs?: number | null } = {},
    ) => {
      setAssetSubmitting(true)
      try {
        const asset = await createShotAudioAsset(projectId, shotId, {
          file,
          filename: file.name,
          kind,
          name: file.name,
          voice,
        })
        setAssets((prev) => [...prev, asset])
        pendingPlacementsRef.current.set(asset.id, {
          trackId,
          atMs: placement.atMs ?? Math.round(controls.currentMs()),
          insertAtStart: placement.insertAtStart === true,
          maxDurationMs: placement.maxDurationMs ?? null,
        })
        startAssetPoll(asset.id)
        return true
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t("timeline.assetFailed"))
        return false
      } finally {
        setAssetSubmitting(false)
      }
    },
    [projectId, shotId, controls, startAssetPoll, t],
  )

  const submitTts = useCallback(
    async (trackId: string, text: string, voice: VoiceSelection) => {
      setAssetSubmitting(true)
      try {
        const asset = await createShotAudioAssetTts(projectId, shotId, { text, voice })
        setAssets((prev) => [...prev, asset])
        pendingPlacementsRef.current.set(asset.id, { trackId, atMs: 0, insertAtStart: true, maxDurationMs: null })
        startAssetPoll(asset.id)
        return true
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t("timeline.assetFailed"))
        return false
      } finally {
        setAssetSubmitting(false)
      }
    },
    [projectId, shotId, startAssetPoll, t],
  )

  // ── Lipsync ──
  // Валидная пара для lipsync: ровно 2 выделенных клипа — один на video-дорожке,
  // один на аудио (version_audio или custom_audio с готовым ассетом).
  const lipsyncSelection = useMemo(() => {
    if (selected.length !== 2) return null
    let video: ClipSelection | null = null
    let audio: ClipSelection | null = null
    for (const sel of selected) {
      const track = displayDoc.tracks.find((tr) => tr.id === sel.trackId)
      const clip = track?.clips.find((c) => c.id === sel.clipId)
      if (!track || !clip) return null
      if (track.kind === "video") {
        if (video || isAnimaticTrack(track)) return null
        video = sel
      } else {
        if (audio) return null
        if (isAnimaticTrack(track)) return null
        if (track.kind === "custom_audio") {
          const asset = clip.audio_asset_id != null ? assets.find((a) => a.id === clip.audio_asset_id) : null
          if (!asset || asset.status !== "ready") return null
        }
        audio = sel
      }
    }
    if (!video || !audio) return null
    if (lockedClipIds.has(video.clipId) || lockedClipIds.has(audio.clipId)) return null
    return { videoClipId: video.clipId, audioClipId: audio.clipId }
  }, [selected, displayDoc, assets, lockedClipIds])

  const handleLipsync = useCallback(async () => {
    const pair = lipsyncSelection
    if (!pair || lipsyncSubmitting) return
    setLipsyncSubmitting(true)
    try {
      // Убедиться, что последние правки сохранены до постановки job.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      await doSaveRef.current(docRef.current)
      await postShotTimelineLipsync(projectId, shotId, pair)
      toast.success(t("timeline.lipsyncStarted"))
      setSelected([])
      onRenderStarted()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("timeline.lipsyncFailed"))
    } finally {
      setLipsyncSubmitting(false)
    }
  }, [projectId, shotId, lipsyncSelection, lipsyncSubmitting, onRenderStarted, t])

  // ── Рендер ──
  const handleRender = useCallback(async () => {
    if (renderSubmitting || renderJobActive) return
    setRenderSubmitting(true)
    try {
      // Убедиться, что последние правки сохранены до постановки job.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      await doSaveRef.current(docRef.current)
      await renderShotTimeline(projectId, shotId)
      toast.success(t("timeline.renderStarted"))
      onRenderStarted()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("timeline.renderFailed"))
    } finally {
      setRenderSubmitting(false)
    }
  }, [projectId, shotId, renderSubmitting, renderJobActive, onRenderStarted, t])

  // ── Шорткаты ──
  useEffect(() => {
    if (!active || correctionTarget) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey) return
      const target = e.target as HTMLElement | null
      if (
        target?.closest(
          'input, textarea, select, [contenteditable="true"], [contenteditable=""], [contenteditable]:not([contenteditable="false"]), .pe-prosemirror, .ProseMirror',
        ) ||
        target?.isContentEditable
      ) {
        return
      }
      const dialog = target?.closest('[role="dialog"], [role="alertdialog"]')
      if (dialog && rootRef.current && !dialog.contains(rootRef.current)) return

      switch (e.code) {
        case "Space":
          e.preventDefault()
          togglePlay()
          return
        case "ArrowLeft":
          e.preventDefault()
          if (e.shiftKey) stepMs(-1000)
          else stepFrames(-1)
          return
        case "ArrowRight":
          e.preventDefault()
          if (e.shiftKey) stepMs(1000)
          else stepFrames(1)
          return
        case "Home":
          e.preventDefault()
          goToStart()
          return
        case "End":
          e.preventDefault()
          goToEnd()
          return
        case "KeyJ":
          e.preventDefault()
          playReverse()
          return
        case "KeyK":
          e.preventDefault()
          pauseAll()
          return
        case "KeyL":
          e.preventDefault()
          playForward()
          return
        case "KeyB":
          e.preventDefault()
          handleBlade()
          return
        case "Delete":
        case "Backspace":
          e.preventDefault()
          handleDeleteSelected()
          return
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [
    active,
    correctionTarget,
    togglePlay,
    stepFrames,
    stepMs,
    goToStart,
    goToEnd,
    playReverse,
    pauseAll,
    playForward,
    handleBlade,
    handleDeleteSelected,
  ])

  // ── Масштаб таймлайна ──
  // Фиксированный зум: длительность контента на масштаб не влияет — при переполнении
  // появляется горизонтальный скролл. Зум меняется только cmd/ctrl + колесо.
  const [pxPerMs, setPxPerMs] = useState(0)
  const pxPerMsRef = useRef(pxPerMs)
  pxPerMsRef.current = pxPerMs
  const pendingZoomRef = useRef<{ anchorMs: number; viewportX: number } | null>(null)
  // Пока зум не трогали руками (cmd/ctrl + колесо), масштаб следует за шириной колонки —
  // иначе стартовый «fit», снятый на более широкой колонке (панель ещё раскрывалась),
  // оставлял дорожки шире контейнера и горизонтальный скролл.
  const userZoomedRef = useRef(false)

  useEffect(() => {
    const el = lanesRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setLaneWidth(entry.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Длительность для «fit» читаем из ref: правка клипов меняет длительность, но не должна
  // менять масштаб под курсором — масштаб следует только за шириной колонки.
  const durationMsRef = useRef(durationMs)
  durationMsRef.current = durationMs

  // Появление контента (пустой таймлайн → клипы загрузились) — тоже повод подогнать масштаб.
  const hasContent = durationMs > 0

  // «Fit» по ширине колонки, пока пользователь сам не менял зум.
  // В студии масштаб всегда от ширины колонки, этот зум не нужен.
  useEffect(() => {
    if (studioLayout || laneWidth <= 0 || userZoomedRef.current) return
    const duration = durationMsRef.current
    const fitDurationMs = Math.max(duration + 1000, duration * 1.05, 3000)
    const fit = (laneWidth - HEADER_W) / fitDurationMs
    setPxPerMs(Math.min(MAX_PX_PER_MS, Math.max(MIN_PX_PER_MS, fit)))
  }, [studioLayout, laneWidth, hasContent])

  useEffect(() => {
    if (!studioLayout || laneWidth <= HEADER_W) return
    setPxPerMs((laneWidth - HEADER_W) / Math.max(durationMs, 1))
  }, [studioLayout, laneWidth, durationMs])

  // Зум по cmd/ctrl + колесо с якорем в точке под курсором.
  useEffect(() => {
    const el = lanesRef.current
    if (!el || studioLayout) return
    const onWheel = (e: WheelEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      e.preventDefault()
      const current = pxPerMsRef.current
      if (current <= 0) return
      const next = Math.min(MAX_PX_PER_MS, Math.max(MIN_PX_PER_MS, current * Math.exp(-e.deltaY * 0.002)))
      if (next === current) return
      const viewportX = e.clientX - el.getBoundingClientRect().left
      const anchorMs = (el.scrollLeft + viewportX - HEADER_W) / current
      userZoomedRef.current = true
      pendingZoomRef.current = { anchorMs, viewportX }
      setPxPerMs(next)
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  }, [studioLayout])

  // После смены зума возвращаем точку таймлайна под курсор (корректировка scrollLeft).
  useLayoutEffect(() => {
    const pending = pendingZoomRef.current
    const el = lanesRef.current
    if (!pending || !el) return
    pendingZoomRef.current = null
    el.scrollLeft = Math.max(0, pending.anchorMs * pxPerMs + HEADER_W - pending.viewportX)
  }, [pxPerMs])

  // Ширина контента дорожек: контент + запас справа, но не меньше видимой области.
  // В студии длина линейки равна длительности, чтобы не появлялся горизонтальный скролл.
  const fittedMs = Math.max(durationMs, 1)
  const viewDurationMs = studioLayout ? fittedMs : Math.max(durationMs + 1000, 3000)
  const laneContentPx = studioLayout ? fittedMs * pxPerMs : Math.max(viewDurationMs * pxPerMs, laneWidth - HEADER_W)

  // Адаптивные major/minor деления линейки (подписи ≥50px, насечки ≥8px).
  const { majorStepS: rulerStepS, divisions: rulerDivisions, minorStepMs: rulerMinorMs } = rulerTickPlan(pxPerMs)

  // ── Скраббинг по линейке ──
  // Через scrub-API движка: сики он коалесит сам (один на кадр анимации),
  // а scrubTo/endScrub дают слышимый скраб (гранулы Web Audio).
  const scrubbingRef = useRef(false)

  const msFromRulerPointer = useCallback(
    (e: React.PointerEvent) => {
      const el = rulerRef.current
      if (!el || pxPerMs <= 0) return null
      const rect = el.getBoundingClientRect()
      return Math.max(0, (e.clientX - rect.left) / pxPerMs)
    },
    [pxPerMs],
  )

  const onRulerPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return
      const ms = msFromRulerPointer(e)
      if (ms == null) return
      e.preventDefault()
      rulerRef.current?.setPointerCapture(e.pointerId)
      scrubbingRef.current = true
      pauseAll()
      controls.scrubTo(ms)
    },
    [msFromRulerPointer, pauseAll, controls],
  )

  const onRulerPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!scrubbingRef.current) return
      const ms = msFromRulerPointer(e)
      if (ms != null) controls.scrubTo(ms)
    },
    [msFromRulerPointer, controls],
  )

  const onRulerPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!scrubbingRef.current) return
      scrubbingRef.current = false
      rulerRef.current?.releasePointerCapture(e.pointerId)
      // Финальный точный сик на позицию отпускания — endScrub применяет последнюю цель.
      const ms = msFromRulerPointer(e)
      if (ms != null) controls.scrubTo(ms)
      controls.endScrub()
    },
    [msFromRulerPointer, controls],
  )

  // Фулскрин-ползунок: scrubTo требует паузы (иначе scrub-rAF и play-tick дерутся
  // за мастер-видео), но, в отличие от линейки, воспроизведение возобновляем —
  // стандартное поведение ползунка видеоплеера.
  const overlayResumeRateRef = useRef<number | null>(null)

  const handleOverlayScrubStart = useCallback(() => {
    overlayResumeRateRef.current = controls.isPlaying() ? controls.getRate() : null
    controls.pause()
  }, [controls])

  const handleOverlayScrubEnd = useCallback(() => {
    controls.endScrub()
    const rate = overlayResumeRateRef.current
    overlayResumeRateRef.current = null
    if (rate != null) controls.play(rate)
  }, [controls])

  // ── Производные ──
  const processingAssets = assets.filter((a) => a.status === "processing")
  const videoTracks = displayDoc.tracks.filter((tr) => tr.kind === "video")
  const versionOrdinalById = useMemo(() => {
    const sorted = [...versions].sort((a, b) => a.id - b.id)
    return new Map(sorted.map((v, i) => [v.id, i + 1]))
  }, [versions])
  const canAddVideo = videoTrackCount(displayDoc) < data.max_video_tracks && versions.length > 0
  const canAddAnimatic = Boolean(data.animatic?.playable)
  const canAddAnimaticVideo = canAddAnimatic && videoTrackCount(displayDoc) < data.max_video_tracks
  const primaryVersion = versions.find((v) => v.id === data.primary_version_id) ?? versions.find((v) => v.active)
  const primaryCorrectionDurationMs =
    primaryVersion == null
      ? null
      : (probedDurationsMs[primaryVersion.id] ??
        versionDurationsRef.current.get(primaryVersion.id) ??
        primaryVersion.duration_ms ??
        null)
  const canAddCorrection =
    videoCorrectionEnabled &&
    canAddVideo &&
    primaryVersion != null &&
    correctionSourceEligible(primaryCorrectionDurationMs)
  const handleAddCorrection = useCallback(() => {
    const versionId = data.primary_version_id
    const duration =
      probedDurationsMs[versionId] ??
      versionDurationsRef.current.get(versionId) ??
      versions.find((v) => v.id === versionId)?.duration_ms
    if (!correctionSourceEligible(duration)) return
    applyDoc(addCorrectionTrack(docRef.current, versionId, duration))
  }, [applyDoc, data.primary_version_id, probedDurationsMs, versions])
  const primarySelected = selected.length > 0 ? selected[selected.length - 1] : null
  const selectedClip = primarySelected
    ? (displayDoc.tracks
        .find((tr) => tr.id === primarySelected.trackId)
        ?.clips.find((c) => c.id === primarySelected.clipId) ?? null)
    : null
  const selectedClipSpeed = selectedClip ? clipSpeed(selectedClip) : null
  const renderBusy = renderSubmitting || renderJobActive
  // Выделенные клипы по дорожкам — для проп TrackLane (React.memo).
  const selectedIdsByTrack = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const sel of selected) {
      const list = map.get(sel.trackId)
      if (list) list.push(sel.clipId)
      else map.set(sel.trackId, [sel.clipId])
    }
    return map
  }, [selected])

  const pipResolve = useMemo(() => {
    if (pipSources == null) return null
    return (timeMs: number) => ({ localMs: Math.max(0, timeMs), sources: pipSources })
  }, [pipSources])

  const trackClipsLocked = (trackId: string) => {
    const track = findTrack(docRef.current, trackId)
    if (!track?.clips.some((clip) => lockedClipIdsRef.current.has(clip.id))) return false
    toast.error(t("timeline.recordTrackLocked"))
    return true
  }

  return (
    <PlayerPipProvider resolve={pipResolve} timeStore={timeStore} playing={playing} getRate={() => controls.getRate()}>
      <div
        ref={setEditorRootEl}
        className={cn("relative flex w-full flex-col gap-2", studioLayout && "h-full min-h-0")}
      >
        {/* ── Превью ── */}
        <div
          ref={shellRef}
          className={cn(
            "group/video relative w-full overflow-hidden bg-black shadow-inner",
            studioLayout && !isFullscreen ? "min-h-32 flex-1 rounded-none" : "max-h-[min(60vh,520px)] rounded-lg",
            isFullscreen && "flex h-full max-h-none items-center justify-center rounded-none",
            // m-0/border-0/p-0 гасят UA-стили popover (шелл поднят в top layer, см. useShellFullscreen).
            isPseudoFullscreen && "fixed inset-0 z-[120] m-0 border-0 p-0",
          )}
          style={isFullscreen || studioLayout ? undefined : { aspectRatio: String(shellAspectRatio) }}
        >
          <div ref={playback.viewportRef} className="absolute inset-0 cursor-pointer" onClick={handleViewportClick} />
          {videoCorner ? <div className="pointer-events-none absolute bottom-2 left-2 z-20">{videoCorner}</div> : null}
          {buffering ? (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white/75 drop-shadow" />
            </div>
          ) : null}
          <StartPlayOverlay playing={playing} timeStore={timeStore} />
          <PlayerPipOverlay />
          <VideoShellOverlay
            shellRef={shellRef}
            isFullscreen={isFullscreen}
            controlsVisible={controlsVisible}
            onControlsPointerEnter={onControlsPointerEnter}
            onControlsPointerLeave={onControlsPointerLeave}
            playing={playing}
            onTogglePlay={togglePlay}
            muted={muted}
            volume={volume}
            setMuted={setMuted}
            setVolume={setVolume}
            toggleFullscreen={toggleFullscreen}
            timeStore={timeStore}
            durationMs={durationMs}
            fps={fps}
            onSeek={(ms) => controls.seek(ms)}
            onScrubStart={handleOverlayScrubStart}
            onScrub={(ms) => controls.scrubTo(ms)}
            onScrubEnd={handleOverlayScrubEnd}
          />
        </div>

        {/* ── Транспорт + тулбар ── */}
        {/* Локальный Provider: тултипы появляются с задержкой только в этом ряду, не по всему приложению. */}
        <TooltipPrimitive.Provider delayDuration={350} skipDelayDuration={0}>
          <div className={cn("@container w-full min-w-0", studioLayout && "shrink-0")}>
            <div className="flex w-full min-w-0 flex-col items-center gap-y-2 @[42rem]:grid @[42rem]:grid-cols-[1fr_auto_1fr] @[42rem]:items-center @[42rem]:gap-x-2 @[42rem]:gap-y-0">
              <div className="flex shrink-0 items-center justify-center gap-2 @[42rem]:justify-self-start">
                <TimecodeDisplay store={timeStore} fps={fps} durationMs={durationMs} title={t("player.timecode")} />
              </div>

              <PlaybackTransport
                className="@[42rem]:justify-self-center"
                playing={playing}
                onTogglePlay={togglePlay}
                onStepFrames={stepFrames}
                onGoToStart={goToStart}
                onGoToEnd={goToEnd}
              >
                <PlayerIconButton
                  label={t("player.loop")}
                  onClick={() =>
                    setLoop((v) => {
                      const next = !v
                      writePlayerLoopPref(next)
                      return next
                    })
                  }
                  icon={<Repeat className="h-4 w-4" />}
                  activeState={loop}
                />
                {afterLoop}
                <PlayerPipButton />
                {hasScrubSources ? (
                  <PlayerIconButton
                    label={t("player.audioScrub")}
                    onClick={() =>
                      setAudioScrub((v) => {
                        const next = !v
                        writePlayerAudioScrubPref(next)
                        return next
                      })
                    }
                    icon={<AudioLines className="h-4 w-4" />}
                    activeState={audioScrub}
                  />
                ) : null}
                {forwardRate > 1 && playing ? (
                  <span className="ml-0.5 text-[10px] font-medium text-ui-accent-text tabular-nums">
                    {forwardRate}x
                  </span>
                ) : null}
              </PlaybackTransport>

              <div className="flex shrink-0 items-center justify-center gap-0.5 @[42rem]:justify-self-end">
                <PlayerIconButton
                  label={t("timeline.snapping")}
                  onClick={() => setSnapEnabled((v) => !v)}
                  icon={<Magnet className="h-4 w-4" />}
                  activeState={snapEnabled}
                />
                {studioLayout || !canAddCorrection ? null : (
                  <PlayerIconButton
                    label={t("timeline.addCorrectionTrack")}
                    onClick={handleAddCorrection}
                    icon={<Wand2 className="h-4 w-4" />}
                  />
                )}
                <PlayerIconButton
                  label={t("timeline.blade")}
                  onClick={handleBlade}
                  icon={<Scissors className="h-4 w-4" />}
                />
                <PlayerIconButton
                  label={t("timeline.deleteClip")}
                  onClick={handleDeleteSelected}
                  icon={<Trash2 className="h-4 w-4" />}
                  disabled={selected.length === 0}
                />
                <ClipSpeedControl speed={selectedClipSpeed} onChange={handleSetClipSpeed} />
                <PlayerIconButton
                  label={t("timeline.lipsyncHint")}
                  onClick={() => void handleLipsync()}
                  icon={
                    lipsyncSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SyncSoIcon className="h-4 w-4" />
                    )
                  }
                  disabled={!lipsyncSelection || lipsyncSubmitting}
                />
                {captureAction ? (
                  <PlayerIconButton
                    label={captureAction.label}
                    onClick={captureAction.onCapture}
                    icon={
                      captureAction.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />
                    }
                    disabled={captureAction.disabled}
                  />
                ) : null}
                <AddTrackMenu
                  versions={versions}
                  audioAssets={assets}
                  canAddVideo={canAddVideo}
                  canAddAnimatic={canAddAnimatic}
                  canAddAnimaticVideo={canAddAnimaticVideo}
                  onAddVideo={(versionId) => {
                    const duration =
                      versionDurationsRef.current.get(versionId) ??
                      versions.find((v) => v.id === versionId)?.duration_ms ??
                      5000
                    applyDoc(addVideoTrack(docRef.current, versionId, duration))
                  }}
                  onAddAudio={() => applyDoc(addCustomAudioTrack(docRef.current))}
                  onAddAudioAsset={(assetId, durationMs) => {
                    applyDoc(addCustomAudioTrackWithAsset(docRef.current, assetId, durationMs))
                  }}
                  onAddAnimatic={(mode: AnimaticMode) => {
                    const animatic = data.animatic
                    if (!animatic?.playable) return
                    applyDoc(
                      addAnimaticTrack(docRef.current, {
                        segmentId: animatic.segment_id,
                        mode,
                        startMs: animatic.start_ms,
                        endMs: animatic.end_ms,
                      }),
                    )
                  }}
                />
                <ActionHintTooltip label={t("timeline.renderHint")}>
                  <button
                    type="button"
                    disabled={renderBusy || durationMs <= 0}
                    onClick={() => void handleRender()}
                    className={cn(
                      "ml-1 flex items-center gap-1.5 rounded-md border border-ui-accent/60 px-2.5 py-1.5 text-[11px] font-medium",
                      "text-ui-accent-text transition-colors hover:border-ui-accent hover:bg-ui-accent/10",
                      "disabled:cursor-not-allowed disabled:opacity-40",
                    )}
                  >
                    {renderBusy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Clapperboard className="h-3.5 w-3.5" />
                    )}
                    {t("timeline.render")}
                  </button>
                </ActionHintTooltip>
                <PlayerIconButton
                  label={t("player.shortcutsAria")}
                  onClick={() => setShortcutsOpen(true)}
                  icon={<CircleHelp className="h-4 w-4" />}
                />
              </div>
            </div>
          </div>
        </TooltipPrimitive.Provider>

        {/* ── Дорожки ── */}
        <div
          ref={lanesRef}
          className={cn(
            "w-full",
            studioLayout
              ? "it2tv-scrollbar max-h-64 shrink-0 overflow-x-hidden overflow-y-auto"
              : "overflow-x-auto pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          )}
          onPointerDown={() => setSelected([])}
        >
          <div
            className="relative flex flex-col gap-1"
            style={{ width: pxPerMs > 0 ? HEADER_W + laneContentPx : undefined }}
          >
            {/* Линейка */}
            <div className="flex items-end gap-0">
              <div className="sticky left-0 z-40 w-[140px] shrink-0 self-stretch bg-ui-bg-elevated" />
              <div
                ref={rulerRef}
                className="relative h-5 min-w-0 flex-1 cursor-ew-resize touch-none overflow-hidden rounded-md bg-ui-surface-sunken/60 select-none"
                onPointerDown={(e) => {
                  e.stopPropagation()
                  onRulerPointerDown(e)
                }}
                onPointerMove={onRulerPointerMove}
                onPointerUp={onRulerPointerUp}
                onPointerCancel={onRulerPointerUp}
              >
                {/* Major (подпись) + minor (насечки) с адаптивной плотностью */}
                {pxPerMs > 0
                  ? Array.from({ length: Math.floor(viewDurationMs / rulerMinorMs) + 1 }, (_, i) => {
                      const isMajor = i % rulerDivisions === 0
                      return (
                        <div
                          key={i}
                          className="pointer-events-none absolute inset-y-0 flex items-end"
                          style={{ left: i * rulerMinorMs * pxPerMs }}
                        >
                          <div
                            className={isMajor ? "h-2 w-px bg-ui-border-strong" : "h-1 w-px bg-ui-border opacity-70"}
                          />
                          {isMajor ? (
                            <span className="ml-0.5 pb-px text-[8px] text-ui-text-muted tabular-nums">
                              {formatRulerLabel((i / rulerDivisions) * rulerStepS)}
                            </span>
                          ) : null}
                        </div>
                      )
                    })
                  : null}
              </div>
            </div>

            {displayDoc.tracks.map((track, trackIndex) => {
              const videoIndex =
                track.kind === "video" ? videoTracks.findIndex((v) => v.id === track.id) + 1 : undefined
              const version =
                track.shot_media_version_id != null
                  ? (versions.find((v) => v.id === track.shot_media_version_id) ?? null)
                  : null
              const canDelete = track.kind !== "video" || videoTracks.length > 1
              // Drag-and-drop: тянущаяся дорожка следует за курсором, остальные
              // в пределах её группы расступаются на шаг ряда.
              const isDraggedRow = trackDragVisual?.fromIndex === trackIndex
              let rowShiftPx = 0
              if (trackDragVisual && !isDraggedRow) {
                const { fromIndex, toIndex } = trackDragVisual
                if (fromIndex < trackIndex && trackIndex <= toIndex) rowShiftPx = -TRACK_ROW_STEP_PX
                if (toIndex <= trackIndex && trackIndex < fromIndex) rowShiftPx = TRACK_ROW_STEP_PX
              }
              return (
                <div
                  key={track.id}
                  className={cn(
                    "flex flex-col gap-1",
                    isDraggedRow && "relative z-50 opacity-90",
                    // Переход только во время drag: на дропе дорожки уже стоят на новых
                    // местах в DOM, и анимация сброса transform дала бы рывок.
                    trackDragVisual && !isDraggedRow && "transition-transform duration-150",
                  )}
                  style={{
                    transform: isDraggedRow
                      ? `translateY(${trackDragVisual.dy}px)`
                      : rowShiftPx !== 0
                        ? `translateY(${rowShiftPx}px)`
                        : undefined,
                  }}
                >
                  <div className="flex items-stretch gap-0">
                    <div
                      className={cn(
                        "sticky left-0 z-40 flex w-[140px] shrink-0 touch-none bg-ui-bg-elevated pr-1 select-none",
                        isDraggedRow ? "cursor-grabbing" : "cursor-grab",
                      )}
                      title={t("timeline.dragToReorder")}
                      onPointerDown={(e) => handleTrackDragDown(track.id, e)}
                      onPointerMove={(e) => handleTrackDragMove(track.id, e)}
                      onPointerUp={(e) => handleTrackDragUp(track.id, e)}
                      onPointerCancel={(e) => handleTrackDragUp(track.id, e)}
                    >
                      <TrackHeader
                        track={track}
                        videoIndex={videoIndex}
                        version={version}
                        versionOrdinal={
                          track.shot_media_version_id != null
                            ? versionOrdinalById.get(track.shot_media_version_id)
                            : undefined
                        }
                        canDelete={canDelete}
                        onTrackAudioChange={handleTrackAudioChange}
                        onToggleHidden={track.kind === "video" ? handleToggleTrackHidden : undefined}
                        onDetachAudio={track.kind === "video" ? handleDetachTrackAudio : undefined}
                        onDelete={handleDeleteTrack}
                        onRenameVersion={track.kind === "video" ? handleRenameVersion : undefined}
                        onOpenRecord={track.kind === "custom_audio" ? handleOpenRecord : undefined}
                        onOpenTts={track.kind === "custom_audio" ? handleOpenTts : undefined}
                        onUpload={track.kind === "custom_audio" ? handleUploadForTrack : undefined}
                      />
                    </div>
                    <TrackLane
                      track={track}
                      pxPerMs={pxPerMs}
                      selectedClipIds={selectedIdsByTrack.get(track.id) ?? EMPTY_SELECTED_IDS}
                      lockedClipIds={lockedClipIds}
                      sources={data.sources}
                      animaticSource={animaticTimelineSource}
                      assets={assets}
                      onSelectClip={handleSelectClip}
                      onMoveClip={handleMoveClip}
                      onResizeClip={handleResizeClip}
                      onRateStretchClip={handleRateStretchClip}
                      onFadeClip={track.kind !== "video" ? handleFadeClip : undefined}
                      onDragEnd={handleClipDragEnd}
                      onActivateCorrectionClip={
                        videoCorrectionEnabled
                          ? (trackId, clipId) => {
                              if (lockedClipIdsRef.current.has(clipId)) return
                              const tr = findTrack(docRef.current, trackId)
                              if (tr?.purpose !== "correction") return
                              setCorrectionTarget({ trackId, clipId })
                            }
                          : undefined
                      }
                      activateCorrectionLabel={t("timeline.openCorrection")}
                    />
                  </div>
                </div>
              )
            })}

            {/* Playhead поверх дорожек (внутри скроллируемого контента) */}
            {pxPerMs > 0 ? (
              <Playhead
                store={timeStore}
                pxPerMs={pxPerMs}
                offsetPx={HEADER_W}
                playingRef={playingRef}
                scrollRef={lanesRef}
              />
            ) : null}
          </div>
        </div>

        {/* ── Ассеты в обработке (ошибки живут в панели «Задачи») ── */}
        {processingAssets.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {processingAssets.map((asset) => (
              <span
                key={asset.id}
                className="flex items-center gap-1.5 rounded-md border border-ui-border bg-ui-surface-sunken px-2 py-1 text-[10px] text-ui-text-secondary"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="max-w-[140px] truncate">{asset.name ?? t("timeline.processingAudio")}</span>
              </span>
            ))}
          </div>
        ) : null}

        <input
          ref={uploadInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            const trackId = uploadTrackIdRef.current
            if (file && trackId) void submitAssetFile(trackId, file, "upload")
            e.target.value = ""
          }}
        />

        {/* ── Диалог шорткатов ── */}
        <Dialog.Root open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[110] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
            <Dialog.Content
              className={cn(
                "ui-dialog-content fixed top-1/2 left-1/2 z-[111] max-h-[min(90vh,560px)] w-[min(96vw,640px)] -translate-x-1/2 -translate-y-1/2",
                "flex flex-col rounded-lg border border-ui-border shadow-2xl shadow-black/50 focus:outline-none dark:bg-zinc-950 light:bg-ui-bg-elevated",
              )}
            >
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ui-border px-4 py-3">
                <Dialog.Title className="text-sm font-semibold text-ui-text-primary">
                  {t("player.shortcutsTitle")}
                </Dialog.Title>
                <Dialog.Close
                  type="button"
                  className="rounded-lg p-1 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                  aria-label={t("common:close")}
                >
                  <X className="h-4 w-4" />
                </Dialog.Close>
              </div>
              <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-3">
                <Dialog.Description className="sr-only">{t("player.shortcutsTitle")}</Dialog.Description>
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {SHORTCUT_SECTIONS.map((section) => (
                    <div key={section.titleKey} className="flex flex-col gap-1">
                      <h3 className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
                        {t(section.titleKey)}
                      </h3>
                      <ul className="flex flex-col">
                        {section.rows.map((row) => (
                          <li
                            key={`${row.labelKey}-${row.keys.map((c) => c.join("+")).join("|")}`}
                            className="flex items-center justify-between gap-2 py-1"
                          >
                            <span className="min-w-0 text-[13px] leading-snug text-ui-text-secondary">
                              {t(row.labelKey)}
                            </span>
                            <span className="flex shrink-0 items-center gap-1">
                              {row.keys.map((combo, comboIndex) => (
                                <span key={combo.join("+")} className="flex items-center gap-0.5">
                                  {comboIndex > 0 ? (
                                    <span className="mx-0.5 text-[10px] text-ui-text-muted">/</span>
                                  ) : null}
                                  {combo.map((key, keyIndex) => (
                                    <span key={`${key}-${keyIndex}`} className="flex items-center gap-0.5">
                                      {keyIndex > 0 ? <span className="text-[10px] text-ui-text-muted">+</span> : null}
                                      <Kbd className="inline px-1 py-0 text-[10px] leading-4">{key}</Kbd>
                                    </span>
                                  ))}
                                </span>
                              ))}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex shrink-0 justify-end border-t border-ui-border px-3 py-2.5">
                <Button type="button" size="sm" variant="ghost" onClick={() => setShortcutsOpen(false)}>
                  {t("common:close")}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {(() => {
          if (!correctionTarget) return null
          const track = findTrack(doc, correctionTarget.trackId)
          const clip = findClip(doc, correctionTarget.trackId, correctionTarget.clipId)
          if (!track || !clip || track.purpose !== "correction") return null
          const versionId = clipVersionId(track, clip)
          if (versionId == null) return null
          const source = data.sources[String(versionId)]
          const streamUrl = source?.stream_url
          if (!streamUrl) return null

          if (videoCorrectionAdvanced) {
            return (
              <SketchDialog
                open
                onOpenChange={(o) => {
                  if (!o) setCorrectionTarget(null)
                }}
                projectId={projectId}
                nested
                showAiPanel={false}
                headerTitle={t("correction.videoTitle")}
                videoCorrection={{
                  streamUrl,
                  captureUrl: source.capture_url ?? source.scrub_url ?? null,
                  sourceVersionId: versionId,
                  sourceInMs: clip.source_in_ms,
                  sourceOutMs: clip.source_out_ms,
                  markupMax: videoCorrectionMarkupMax,
                  correctionTrackId: track.id,
                  correctionClipId: clip.id,
                  timelineStartMs: clip.timeline_start_ms,
                  shotId,
                  durationMs: source.duration_ms ?? null,
                  frameRate: source.frame_rate ?? null,
                  aspectRatio: source.aspect_ratio ?? null,
                  timelineFrameUrls: source.timeline_frame_urls ?? [],
                  onQueued: () => {
                    markCorrectionClipPending(clip.id)
                    setCorrectionTarget(null)
                    onCorrectionQueued?.()
                  },
                  onQueueFailed: () => {
                    clearCorrectionClipPending(clip.id)
                  },
                }}
              />
            )
          }

          return (
            <CorrectionPromptDialog
              open
              onOpenChange={(o) => {
                if (!o) setCorrectionTarget(null)
              }}
              nested
              title={t("correction.videoTitle")}
              hint={t("correction.videoDescription")}
              placeholder={t("placeholders.correctionVideo")}
              versionVideoUrl={streamUrl}
              closeAriaLabel={t("common.close")}
              cancelLabel={t("common.cancel")}
              generateLabel={t("correction.startCorrection")}
              submittingLabel={t("common.submitting")}
              onSubmit={async (prompt) => {
                const csrf = getCsrfToken()
                const body: Record<string, unknown> = {
                  authenticity_token: csrf,
                  correction_prompt: prompt,
                  source_version_id: versionId,
                  trim_start_ms: clip.source_in_ms,
                  trim_end_ms: clip.source_out_ms,
                  correction_track_id: track.id,
                  correction_clip_id: clip.id,
                  timeline_start_ms: clip.timeline_start_ms,
                }
                const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/correct_video`, {
                  method: "POST",
                  headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrf,
                    "X-Requested-With": "XMLHttpRequest",
                  },
                  credentials: "same-origin",
                  body: JSON.stringify(body),
                })
                const dataJson = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
                if (!res.ok || !dataJson.ok) {
                  throw new Error(dataJson.error ?? t("correction.startFailed"))
                }
                markCorrectionClipPending(clip.id)
                setCorrectionTarget(null)
                onCorrectionQueued?.()
              }}
            />
          )
        })()}
        {composeSession?.mode === "record" ? (
          <AudioRecordDialog
            key={`record-${composeSession.trackId}`}
            projectId={projectId}
            voices={voices}
            stsProviderKinds={stsProviderKinds}
            busy={assetSubmitting}
            portalTarget={composePortalTarget}
            onClose={() => setComposeSession(null)}
            onPauseForDub={() => controls.pause()}
            onRewindForDub={() => controls.seek(0)}
            onPlayForDub={() => {
              // Флаг глушит до play(): applyFrame ставит el.muted раньше, чем элемент стартует.
              controls.setMasterMuted(true)
              setDubSilent(true)
              controls.play(1)
            }}
            onReleaseDubSilence={() => setDubSilent(false)}
            shotSync={shotDubSync}
            onReadPlayhead={() => controls.currentMs()}
            shotPlaying={playing}
            onSave={(file, voice, maxDurationMs) => {
              if (trackClipsLocked(composeSession.trackId)) return Promise.resolve(false)
              return submitAssetFile(composeSession.trackId, file, "recording", voice, {
                insertAtStart: true,
                maxDurationMs,
              })
            }}
          />
        ) : null}
        {composeSession?.mode === "tts" ? (
          <AudioTtsDialog
            key={`tts-${composeSession.trackId}`}
            projectId={projectId}
            shotId={shotId}
            voices={voices}
            defaultLibraryVoice={defaultLibraryVoice}
            ttsProviderKind={ttsProviderKind}
            busy={assetSubmitting}
            portalTarget={composePortalTarget}
            onClose={() => setComposeSession(null)}
            onGenerate={(text, voice) => {
              if (trackClipsLocked(composeSession.trackId)) return Promise.resolve(false)
              return submitTts(composeSession.trackId, text, voice)
            }}
          />
        ) : null}
      </div>
    </PlayerPipProvider>
  )
}
